import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Pie, Bar, Line } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale, LineElement, PointElement, Filler } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { motion } from 'framer-motion';
import { extendPalette } from './constants';

ChartJS.register(ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale, LineElement, PointElement, Filler, ChartDataLabels);

function YearlySummaries({ budgetData, summaryData, setSummaryData, refreshData }) {
  const [selectedYear, setSelectedYear] = useState(new Date());
  const [yearlyData, setYearlyData] = useState({ total: 0, income: 0, byCategory: {}, byPayment: {}, months: {} });
  const [isLoadingYearly, setIsLoadingYearly] = useState(false);
  const latestYearRequest = useRef(0);

  const handleYearChange = useCallback((date) => {
    console.log('Year Picker changed to:', date);
    setSelectedYear(date);
  }, []);

  const fetchYearlySummaries = useCallback(async (year) => {
    // Only the latest year requested may update state (fast year switching)
    const requestId = ++latestYearRequest.current;
    const isStale = () => requestId !== latestYearRequest.current;
    setIsLoadingYearly(true);
    try {
      console.log(`Fetching yearly data for ${year}...`);
      // Use the new efficient endpoint for yearly data
      const response = await axios.get(`/api/yearly-data/${year}`);
      if (isStale()) return;
      const data = response.data || { total: 0, income: 0, byCategory: {}, byPayment: {}, months: {} };
      console.log('Yearly data fetched:', data);
      setYearlyData(data);
    } catch (err) {
      // If new endpoint fails, fall back to legacy endpoint
      try {
        console.log(`Falling back to legacy endpoint for year ${year}...`);
        const response = await axios.get(`/api/yearly-summaries?year=${year}`);
        if (isStale()) return;
        const data = response.data || { total: 0, income: 0, byCategory: {}, byPayment: {}, months: {} };
        console.log('Yearly data fetched (legacy):', data);
        setYearlyData(data);
      } catch (fallbackErr) {
        if (isStale()) return;
        console.error('Error fetching yearly summaries (both endpoints failed):', fallbackErr.message);
        setYearlyData({ total: 0, income: 0, byCategory: {}, byPayment: {}, months: {} });
      }
    } finally {
      if (!isStale()) setIsLoadingYearly(false);
    }
  }, []);

  useEffect(() => {
    const year = selectedYear.getFullYear();
    fetchYearlySummaries(year);
  }, [selectedYear, fetchYearlySummaries]);

  const chartDataCategories = useMemo(() => {
    const labels = Object.keys(yearlyData.byCategory || {}).filter(key => yearlyData.byCategory[key] > 0);
    const values = Object.values(yearlyData.byCategory || {}).filter(val => val > 0);
    
    // Premium glassmorphism gradient colors for yearly view
    const glassmorphismColors = [
      'rgba(139, 92, 246, 0.8)', // Purple
      'rgba(59, 130, 246, 0.8)', // Blue  
      'rgba(16, 185, 129, 0.8)', // Green
      'rgba(245, 158, 11, 0.8)', // Yellow
      'rgba(239, 68, 68, 0.8)', // Red
      'rgba(168, 85, 247, 0.8)', // Violet
      'rgba(34, 197, 94, 0.8)', // Emerald
      'rgba(251, 191, 36, 0.8)', // Amber
      'rgba(244, 63, 94, 0.8)', // Rose
      'rgba(99, 102, 241, 0.8)', // Indigo
      'rgba(6, 182, 212, 0.8)', // Cyan
      'rgba(132, 204, 22, 0.8)', // Lime
      'rgba(217, 70, 239, 0.8)' // Fuchsia
    ];
    
    const borderColors = [
      'rgba(139, 92, 246, 1)',
      'rgba(59, 130, 246, 1)',
      'rgba(16, 185, 129, 1)',
      'rgba(245, 158, 11, 1)',
      'rgba(239, 68, 68, 1)',
      'rgba(168, 85, 247, 1)',
      'rgba(34, 197, 94, 1)',
      'rgba(251, 191, 36, 1)',
      'rgba(244, 63, 94, 1)',
      'rgba(99, 102, 241, 1)',
      'rgba(6, 182, 212, 1)',
      'rgba(132, 204, 22, 1)',
      'rgba(217, 70, 239, 1)'
    ];
    
    return {
      labels,
      datasets: [{
        data: values,
        backgroundColor: extendPalette(glassmorphismColors, labels.length),
        borderColor: extendPalette(borderColors, labels.length, { lightness: 40, alpha: 1 }),
        borderWidth: 2,
        hoverBackgroundColor: extendPalette(glassmorphismColors, labels.length).map(color =>
          color.replace(/0\.8\)$/, '1)')
        ),
        hoverBorderColor: '#ffffff',
        hoverBorderWidth: 3
      }]
    };
  }, [yearlyData]);

  const incomeVsSpendingChart = useMemo(() => {
    return {
      labels: ['Spending', 'Income'],
      datasets: [{
        data: [yearlyData.total || 0, yearlyData.income || 0],
        backgroundColor: [
          'rgba(239, 68, 68, 0.8)', // Spending - red with transparency
          'rgba(16, 185, 129, 0.8)'  // Income - green with transparency
        ],
        borderColor: [
          'rgba(239, 68, 68, 1)',
          'rgba(16, 185, 129, 1)'
        ],
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
        hoverBackgroundColor: [
          'rgba(239, 68, 68, 1)',
          'rgba(16, 185, 129, 1)'
        ],
        hoverBorderColor: '#ffffff',
        hoverBorderWidth: 3
      }]
    };
  }, [yearlyData]);

  const monthlyTrendChart = useMemo(() => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const spendingData = months.map(month => yearlyData.months[month]?.total || 0);
    const incomeData = months.map(month => yearlyData.months[month]?.income || 0);
    return {
      labels: months.map(m => m.slice(0, 3)),
      datasets: [
        { 
          label: 'Spending', 
          data: spendingData, 
          borderColor: 'rgba(239, 68, 68, 1)', 
          backgroundColor: (context) => {
            const chart = context.chart;
            const {ctx, chartArea} = chart;
            if (!chartArea) return null;
            const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            gradient.addColorStop(0, 'rgba(239, 68, 68, 0.4)');
            gradient.addColorStop(0.5, 'rgba(239, 68, 68, 0.2)');
            gradient.addColorStop(1, 'rgba(239, 68, 68, 0.05)');
            return gradient;
          },
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: 'rgba(239, 68, 68, 1)',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: 'rgba(239, 68, 68, 1)',
          pointHoverBorderColor: '#ffffff',
          pointHoverBorderWidth: 3
        },
        { 
          label: 'Income', 
          data: incomeData, 
          borderColor: 'rgba(16, 185, 129, 1)', 
          backgroundColor: (context) => {
            const chart = context.chart;
            const {ctx, chartArea} = chart;
            if (!chartArea) return null;
            const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            gradient.addColorStop(0, 'rgba(16, 185, 129, 0.4)');
            gradient.addColorStop(0.5, 'rgba(16, 185, 129, 0.2)');
            gradient.addColorStop(1, 'rgba(16, 185, 129, 0.05)');
            return gradient;
          },
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: 'rgba(16, 185, 129, 1)',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: 'rgba(16, 185, 129, 1)',
          pointHoverBorderColor: '#ffffff',
          pointHoverBorderWidth: 3
        }
      ]
    };
  }, [yearlyData]);

  const quarterlyChart = useMemo(() => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const quarters = { Q1: { income: 0, total: 0 }, Q2: { income: 0, total: 0 }, Q3: { income: 0, total: 0 }, Q4: { income: 0, total: 0 } };
    Object.entries(yearlyData.months || {}).forEach(([month, data]) => {
      const monthIdx = months.indexOf(month);
      const q = Math.floor(monthIdx / 3);
      quarters[`Q${q + 1}`].income += data.income || 0;
      quarters[`Q${q + 1}`].total += data.total || 0;
    });
    return {
      labels: ['Q1', 'Q2', 'Q3', 'Q4'],
      datasets: [
        { 
          label: 'Spending', 
          data: Object.values(quarters).map(q => q.total), 
          backgroundColor: 'rgba(239, 68, 68, 0.8)',
          borderColor: 'rgba(239, 68, 68, 1)',
          borderWidth: 2,
          borderRadius: 8,
          borderSkipped: false,
          hoverBackgroundColor: 'rgba(239, 68, 68, 1)',
          hoverBorderColor: '#ffffff',
          hoverBorderWidth: 3
        },
        { 
          label: 'Income', 
          data: Object.values(quarters).map(q => q.income), 
          backgroundColor: 'rgba(16, 185, 129, 0.8)',
          borderColor: 'rgba(16, 185, 129, 1)',
          borderWidth: 2,
          borderRadius: 8,
          borderSkipped: false,
          hoverBackgroundColor: 'rgba(16, 185, 129, 1)',
          hoverBorderColor: '#ffffff',
          hoverBorderWidth: 3
        }
      ]
    };
  }, [yearlyData]);

  const monthlyVariance = useMemo(() => {
    // Ensure yearlyData.months exists and has entries
    if (!yearlyData.months || Object.keys(yearlyData.months).length === 0) {
      return [];
    }

    // Calculate averages
    const yearlyAvgSpending = yearlyData.total / 12;
    const yearlyAvgIncome = yearlyData.income / 12;
    
    // Safely process the months data
    return Object.entries(yearlyData.months).filter(([_, data]) => data && typeof data === 'object').map(([month, data]) => {
      // Default values if data properties are missing
      const total = data?.total || 0;
      const income = data?.income || 0;
      
      // Calculate change percentages
      const spendingChange = yearlyAvgSpending ? ((total - yearlyAvgSpending) / yearlyAvgSpending * 100).toFixed(1) : 0;
      const incomeChange = yearlyAvgIncome ? ((income - yearlyAvgIncome) / yearlyAvgIncome * 100).toFixed(1) : 0;
      
      return { 
        month: typeof month === 'string' ? `${month.slice(0, 3)}${data.live ? ' (so far)' : ''}` : 'Unk', 
        spending: total, 
        income: income, 
        spendingChange, 
        incomeChange 
      };
    });
  }, [yearlyData]);

  const profitLoss = yearlyData.income !== undefined && yearlyData.total !== undefined 
    ? yearlyData.income - yearlyData.total 
    : 0;
  const savingsRate = yearlyData.income && yearlyData.income > 0 
    ? ((yearlyData.income - yearlyData.total) / yearlyData.income * 100).toFixed(1) 
    : 0;

  const allCategories = useMemo(() => {
    return Object.entries(yearlyData.byCategory || {})
      .sort((a, b) => b[1] - a[1])
      .map(([cat, val]) => ({ category: cat, amount: val }));
  }, [yearlyData]);

  // Enhanced glassmorphism chart options for yearly summaries
  const glassmorphismChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: 'rgba(255, 255, 255, 0.9)',
          font: {
            family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            size: 12,
            weight: 500
          },
          boxWidth: 15,
          padding: 15,
          usePointStyle: true
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(10px)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        cornerRadius: 12,
        padding: 12,
        titleFont: {
          family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          size: 13,
          weight: 600
        },
        bodyFont: {
          family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          size: 12
        },
        displayColors: true,
        callbacks: {
          label: (context) => {
            const label = context.dataset.label || '';
            const value = context.raw || 0;
            return `${label}: ₪${value.toLocaleString('en-US')}`;
          }
        }
      },
      datalabels: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
          tickBorderDash: [2, 4]
        },
        border: {
          display: false
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.8)',
          font: {
            family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            size: 11
          },
          padding: 8,
          callback: (value) => `₪${value.toLocaleString('en-US')}`
        }
      },
      x: {
        grid: {
          display: false
        },
        border: {
          display: false
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.8)',
          font: {
            family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            size: 11
          },
          padding: 8
        }
      }
    },
    animation: {
      duration: 1500,
      easing: 'easeOutQuart'
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          color: 'rgba(255, 255, 255, 0.9)',
          font: {
            family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            size: 11,
            weight: 500
          },
          boxWidth: 15,
          padding: 10,
          usePointStyle: true
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(10px)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        cornerRadius: 12,
        padding: 12,
        titleFont: {
          family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          size: 13,
          weight: 600
        },
        bodyFont: {
          family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          size: 12
        },
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.raw || 0;
            const percentage = ((value / yearlyData.total) * 100).toFixed(1);
            return `${label}: ₪${value.toLocaleString('en-US')} (${percentage}%)`;
          }
        }
      },
      datalabels: {
        display: false
      }
    },
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 2000,
      easing: 'easeOutQuart'
    }
  };

  return (
    <motion.div 
      className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 p-4 md:p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <div className="w-full max-w-6xl mx-auto">
        {/* Page Header with Year Selector */}
        <header className="mb-8 backdrop-blur-xl bg-white/10 border border-white/20 rounded-xl shadow-2xl p-6 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Yearly Budget Summary</h1>
          <p className="text-white/70 mb-6">Track and analyze your financial performance over time</p>
          <div className="inline-block relative">
            <select
              value={selectedYear.getFullYear()}
              onChange={(e) => handleYearChange(new Date(e.target.value, 0, 1))}
              className="appearance-none bg-white/20 border border-white/30 text-white py-3 px-8 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 cursor-pointer pr-10 transition-all duration-200 hover:bg-white/30"
              style={{ position: 'relative', zIndex: 1000 }}
            >
              {Array.from({ length: new Date().getFullYear() - 2019 }, (_, i) => 2020 + i).map(year => (
                <option key={year} value={year} style={{ background: 'rgba(0, 0, 0, 0.9)', color: 'white' }}>{year}</option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-white">
              <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
              </svg>
            </div>
          </div>
        </header>

        {isLoadingYearly ? (
          <motion.div 
            className="flex justify-center items-center h-64"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="animate-pulse flex flex-col items-center">
              <div className="w-12 h-12 rounded-full border-4 border-white/30 border-t-transparent animate-spin mb-4"></div>
              <p className="text-white/70 font-medium">Loading yearly data...</p>
            </div>
          </motion.div>
        ) : Object.keys(yearlyData).length > 0 ? (
          <motion.div 
            className="space-y-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            {/* Key Financial Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-xl shadow-2xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:bg-white/20 transform hover:-translate-y-1">
                <div className="p-1 bg-green-500/70"></div>
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-white/70 uppercase tracking-wider mb-1">Income</h3>
                  <p className="text-2xl font-bold text-white">
                    ₪{(yearlyData.income || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </p>
                  <p className="text-xs text-white/60 mt-1">Total yearly income</p>
                </div>
              </div>
              <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-xl shadow-2xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:bg-white/20 transform hover:-translate-y-1">
                <div className="p-1 bg-red-500/70"></div>
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-white/70 uppercase tracking-wider mb-1">Spending</h3>
                  <p className="text-2xl font-bold text-white">
                    ₪{(yearlyData.total || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </p>
                  <p className="text-xs text-white/60 mt-1">Total yearly expenses</p>
                </div>
              </div>
              <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-xl shadow-2xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:bg-white/20 transform hover:-translate-y-1">
                <div className={`p-1 ${profitLoss >= 0 ? 'bg-green-500/70' : 'bg-red-500/70'}`}></div>
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-white/70 uppercase tracking-wider mb-1">Net Savings</h3>
                  <p className={`text-2xl font-bold ${profitLoss >= 0 ? 'text-white' : 'text-red-300'}`}>
                    ₪{profitLoss.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </p>
                  <p className="text-xs text-white/60 mt-1">Income minus expenses</p>
                </div>
              </div>
              <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-xl shadow-2xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:bg-white/20 transform hover:-translate-y-1">
                <div className={`p-1 ${savingsRate >= 20 ? 'bg-green-500/70' : savingsRate >= 0 ? 'bg-yellow-500/70' : 'bg-red-500/70'}`}></div>
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-white/70 uppercase tracking-wider mb-1">Savings Rate</h3>
                  <p className={`text-2xl font-bold ${savingsRate >= 20 ? 'text-white' : savingsRate >= 0 ? 'text-yellow-300' : 'text-red-300'}`}>
                    {savingsRate}%
                  </p>
                  <p className="text-xs text-white/60 mt-1">Percentage of income saved</p>
                </div>
              </div>
            </div>

            {/* Income vs Spending & Monthly Trends */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-xl shadow-2xl p-6 transition-all duration-300 hover:shadow-xl hover:bg-white/20">
                <h3 className="text-lg font-semibold text-white mb-4">Income vs Spending</h3>
                <div>
                  <motion.div
                    className="chart-container"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.1 }}
                    style={{ position: 'relative', zIndex: 1 }}
                  >
                    <Bar 
                      data={incomeVsSpendingChart} 
                      options={{
                        ...glassmorphismChartOptions,
                        plugins: {
                          ...glassmorphismChartOptions.plugins,
                          legend: { display: false }
                        }
                      }}
                      height={220}
                    />
                  </motion.div>
                </div>
              </div>
              <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-xl shadow-2xl p-6 transition-all duration-300 hover:shadow-xl hover:bg-white/20">
                <h3 className="text-lg font-semibold text-white mb-4">Monthly Trends</h3>
                <motion.div
                  className="chart-container"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  style={{ position: 'relative', zIndex: 1 }}
                >
                  <Line 
                    data={monthlyTrendChart} 
                    options={{
                      ...glassmorphismChartOptions,
                      scales: {
                        ...glassmorphismChartOptions.scales,
                        y: {
                          ...glassmorphismChartOptions.scales.y,
                          ticks: {
                            ...glassmorphismChartOptions.scales.y.ticks,
                            callback: (value) => `₪${value.toLocaleString('en-US', { notation: 'compact', compactDisplay: 'short' })}`
                          }
                        }
                      }
                    }}
                    height={220}
                  />
                </motion.div>
              </div>
            </div>

            {/* Quarterly Chart & Spending Categories */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-xl shadow-2xl p-6 transition-all duration-300 hover:shadow-xl hover:bg-white/20">
                <h3 className="text-lg font-semibold text-white mb-4">Quarterly Breakdown</h3>
                <motion.div
                  className="chart-container"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  style={{ position: 'relative', zIndex: 1 }}
                >
                  <Bar 
                    data={quarterlyChart} 
                    options={{
                      ...glassmorphismChartOptions,
                      scales: {
                        ...glassmorphismChartOptions.scales,
                        y: {
                          ...glassmorphismChartOptions.scales.y,
                          ticks: {
                            ...glassmorphismChartOptions.scales.y.ticks,
                            callback: (value) => `₪${value.toLocaleString('en-US', { notation: 'compact', compactDisplay: 'short' })}`
                          }
                        }
                      }
                    }}
                    height={220}
                  />
                </motion.div>
              </div>
              <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-xl shadow-2xl p-6 transition-all duration-300 hover:shadow-xl hover:bg-white/20">
                <h3 className="text-lg font-semibold text-white mb-4">Spending by Category</h3>
                {chartDataCategories.labels.length > 0 ? (
                  <motion.div
                    className="chart-container"
                    initial={{ opacity: 0, rotate: -10 }}
                    animate={{ opacity: 1, rotate: 0 }}
                    transition={{ duration: 1, delay: 0.4 }}
                    style={{ position: 'relative', zIndex: 1 }}
                  >
                    <Pie 
                      data={chartDataCategories} 
                      options={pieChartOptions}
                      height={220}
                    />
                  </motion.div>
                ) : (
                  <div className="flex justify-center items-center h-32">
                    <p className="text-white/70">No spending data available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Month-by-Month Variance Table */}
            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-xl shadow-2xl p-6 transition-all duration-300 hover:shadow-xl hover:bg-white/20">
              <h3 className="text-lg font-semibold text-white mb-4">Monthly Variance Analysis</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="bg-white/10 border-b border-white/20">
                      <th className="px-4 py-3 font-semibold text-white/90">Month</th>
                      <th className="px-4 py-3 font-semibold text-white/90 text-right">Expenses</th>
                      <th className="px-4 py-3 font-semibold text-white/90 text-right">VS Avg</th>
                      <th className="px-4 py-3 font-semibold text-white/90 text-right">Income</th>
                      <th className="px-4 py-3 font-semibold text-white/90 text-right">VS Avg</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyVariance.map((row, idx) => (
                      <tr key={idx} className="border-b border-white/10 hover:bg-white/10 transition-colors duration-150">
                        <td className="px-4 py-3 font-medium text-white">{row.month}</td>
                        <td className="px-4 py-3 text-right text-white/90">₪{row.spending.toLocaleString('en-US')}</td>
                        <td className={`px-4 py-3 text-right font-medium ${parseFloat(row.spendingChange) > 0 ? 'text-red-300' : 'text-green-300'}`}>
                          {row.spendingChange > 0 ? '+' : ''}{row.spendingChange}%
                        </td>
                        <td className="px-4 py-3 text-right text-white/90">₪{row.income.toLocaleString('en-US')}</td>
                        <td className={`px-4 py-3 text-right font-medium ${parseFloat(row.incomeChange) > 0 ? 'text-green-300' : 'text-red-300'}`}>
                          {row.incomeChange > 0 ? '+' : ''}{row.incomeChange}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* All Categories */}
            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-xl shadow-2xl p-6 transition-all duration-300 hover:shadow-xl hover:bg-white/20">
              <h3 className="text-lg font-semibold text-white mb-4">Expense Categories Breakdown</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {allCategories.map(({ category, amount }, index) => (
                  <div key={category} className="p-3 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 transition-colors duration-200">
                    <div className="flex justify-between items-center">
                      <span className="text-white font-medium truncate">{category}</span>
                      <span className="ml-1 font-semibold text-white">₪{amount.toLocaleString('en-US')}</span>
                    </div>
                    <div className="mt-2 w-full bg-white/20 rounded-full h-1.5">
                      <div 
                        className="bg-gradient-to-r from-blue-400 to-purple-500 h-1.5 rounded-full" 
                        style={{ width: `${Math.min(100, (amount / yearlyData.total) * 100)}%` }}
                      ></div>
                    </div>
                    <div className="mt-1 text-xs text-white/70 text-right">
                      {((amount / yearlyData.total) * 100).toFixed(1)}% of total
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div 
            className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-xl shadow-2xl p-8 text-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <svg className="w-16 h-16 mx-auto text-white/50 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            <h3 className="text-xl font-medium text-white mb-2">No Data Available</h3>
            <p className="text-white/70 mb-4">No financial data is available for the selected year.</p>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

export default YearlySummaries;